import { moveToFulfilled } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import type {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import type { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import type {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import type { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import type {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';

import type { ModeratorUsers } from './types';

export type ModeratorUserAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction;

const events = [
  'FEED.LOAD_FULFILLED',
  'FEED.LOAD_NEWER_THREADS_FULFILLED',
  'FEED.LOAD_OLDER_THREADS_FULFILLED',
  'SEARCH.LOAD_FULFILLED',
  'SEARCH.LOAD_MORE_FULFILLED',
  'THREAD.PIN_TO_EVENT_FULFILLED',
  'THREAD.PIN_TO_GROUP_FULFILLED',
  'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED',
  'THREAD.UNPIN_FROM_EVENT_FULFILLED',
  'THREAD.UNPIN_FROM_GROUP_FULFILLED',
  'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED',
  'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED',
  'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED',
  'NESTED_THREAD.LOAD_FULFILLED',
  'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED',
  'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED',
  'NESTED_THREAD_LIST.LOAD_FULFILLED',
  'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED',
  'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED',
  'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED',
  'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED',
  'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED',
  'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED',
  'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED',
  'THREAD_SHARES.LOAD_FULFILLED',
];

export const initialState: ModeratorUsers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<ModeratorUsers, ModeratorUserAction> = (state = initialState, action) => {
  if (events.includes(action.type)) {
    return moveToFulfilled(state, action.payload.entities.moderatorUsers);
  }

  return state;
};

export default reducer;
